import React, { useMemo, useState } from 'react';
import { Box, Grid, Menu, MenuItem, Typography } from '@material-ui/core';
import { CompareArrows, ExpandMore } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { unpackToDate } from '@campfire/hot-date';
import { CompletedTaskFormFieldList } from '../../../../../common/form/task-form/CompletedTaskViewer';
import { TaskItemsDeserialize } from '../../../../admin/admin-console/admin-console-content-pages/admin-console-volunteer-profile/form-builder/utils';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';

export interface CompletedTaskVersion {
  completedTaskId: string;
  dateSubmitted: string;
  status: string;
  taskFieldValues: any[];
}

const statusLabel = (status: string) => {
  switch (status) {
    case 'admin-approved':
      return 'Admin approved';
    case 'not-applicable':
      return 'Not applicable';
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
};

const versionDateLabel = (version: CompletedTaskVersion) => unpackToDate(version.dateSubmitted).toFormat('d LLL y');

const versionLabel = (version: CompletedTaskVersion) => `${versionDateLabel(version)} · ${statusLabel(version.status)}`;

const keyValues = (version?: CompletedTaskVersion) =>
  (version?.taskFieldValues ?? []).reduce(
    (acc, curr) => ({
      ...acc,
      [curr.field.taskFieldId]: curr,
    }),
    {}
  );

interface CompletedTaskHistoryProps {
  versions: CompletedTaskVersion[];
  selectedVersionId: string | null;
  onSelectVersion: (completedTaskId: string | null) => void;
  compareMode: boolean;
  onToggleCompare: () => void;
}

export const CompletedTaskHistory = (props: CompletedTaskHistoryProps) => {
  const { versions, selectedVersionId, onSelectVersion, compareMode, onToggleCompare } = props;
  const { theme } = useCampfireTheme();
  const [versionMenuAnchorEl, setVersionMenuAnchorEl] = useState<null | HTMLElement>(null);

  if (versions.length < 2) return null;

  const foundIndex = selectedVersionId
    ? versions.findIndex((version) => version.completedTaskId === selectedVersionId)
    : 0;
  const selectedIndex = foundIndex === -1 ? 0 : foundIndex;
  const selectedVersion = versions[selectedIndex];

  const buttonStyle = {
    border: '1px solid #9e9e9e',
    boxSizing: 'border-box' as const,
    borderRadius: '4px',
    padding: '2px 7px',
    marginLeft: '8px',
  };

  return (
    <Box marginBottom={2}>
      <Box display='flex' alignItems='center' justifyContent='flex-end' marginBottom={1}>
        <TabletButton
          size='small'
          variant='outlined'
          color='primary'
          aria-controls='completed-task-version-menu'
          aria-haspopup='true'
          onClick={(event) => setVersionMenuAnchorEl(event.currentTarget)}
          endIcon={<ExpandMore />}
          style={{ ...buttonStyle, color: theme.color.grey.neutral300 }}
        >
          {selectedIndex === 0 ? `Latest · ${versionDateLabel(selectedVersion)}` : versionDateLabel(selectedVersion)}
        </TabletButton>
        <TabletButton
          size='small'
          variant='outlined'
          color='primary'
          onClick={onToggleCompare}
          startIcon={<CompareArrows />}
          style={{
            ...buttonStyle,
            backgroundColor: compareMode ? theme.color.grey.neutral200 : 'transparent',
            color: compareMode ? '#f6f8f9' : theme.color.grey.neutral300,
          }}
        >
          {'Compare'}
        </TabletButton>
      </Box>
      <Menu
        id='completed-task-version-menu'
        anchorEl={versionMenuAnchorEl}
        keepMounted
        open={Boolean(versionMenuAnchorEl)}
        onClose={() => setVersionMenuAnchorEl(null)}
      >
        {versions.map((version, index) => (
          <MenuItem
            key={version.completedTaskId}
            selected={index === selectedIndex}
            onClick={() => {
              setVersionMenuAnchorEl(null);
              onSelectVersion(index === 0 ? null : version.completedTaskId);
            }}
          >
            {index === 0 ? `Latest · ${versionLabel(version)}` : versionLabel(version)}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

interface CompletedTaskCompareProps {
  taskItems: TaskItemsDeserialize;
  baseline: CompletedTaskVersion;
  current: CompletedTaskVersion;
  isTaskZero: boolean;
}

export const CompletedTaskCompare = (props: CompletedTaskCompareProps) => {
  const { taskItems, baseline, current, isTaskZero } = props;
  const { theme } = useCampfireTheme();

  const baselineValues = useMemo(() => keyValues(baseline), [baseline]);
  const currentValues = useMemo(() => keyValues(current), [current]);

  const columnHeader = (label: string, version: CompletedTaskVersion) => (
    <Box paddingY={1} paddingX={1.5} marginBottom={1} bgcolor={theme.color.grey.neutralBrand100} borderRadius={4}>
      <Typography variant='caption' style={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </Typography>
      <Typography variant='body2' color='textSecondary'>
        {versionLabel(version)}
      </Typography>
    </Box>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        {columnHeader('Compared with', baseline)}
        <CompletedTaskFormFieldList
          taskItems={taskItems}
          taskItemValues={baselineValues}
          isTaskZero={isTaskZero}
          completedTasks={[]}
        />
      </Grid>
      <Grid item xs={6}>
        {columnHeader('Latest', current)}
        <CompletedTaskFormFieldList
          taskItems={taskItems}
          taskItemValues={currentValues}
          isTaskZero={isTaskZero}
          completedTasks={[]}
        />
      </Grid>
    </Grid>
  );
};
